import { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Divider,
  Button,
  Toolbar,
  IconButton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate, useParams, Link } from 'react-router';
import { ArrowBack } from '@mui/icons-material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import { LoadingButton } from '@mui/lab';
import { useUser } from '@/providers/UserContext';
import LoadingMask from '@/components/shared/LoadingMask';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';

import { useApi } from '@/providers/ApiContext';
import {
  getChart,
  deleteChart,
  approveChart,
  rejectChart,
  submitChart,
  generateChartElements,
} from '@/actions/chart';
import ChartDeleteDialog from '../ChartDeleteDialog';
import toNiceTimestamp from '@/util/toNiceTimestamp';
import { useDocTypes } from '@/providers/DocTypesContext';
import ChartWaypoints from './ChartWaypoints';
import ChartContents from './ChartContents';
import { getChartWaypoints } from '@/actions/chartWaypoint';
import type { Chart, ChartWaypoint, EntityId } from '@/types';

function AdminChart(): React.JSX.Element {
  const user = useUser();
  const api = useApi();
  const navigate = useNavigate();
  const doc_types = useDocTypes();
  const { chart_id } = useParams<{ chart_id: string }>();
  const [chart, setChart] = useState<Chart | null>(null);
  const [loading_chart, setLoadingChart] = useState<boolean>(true);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [api_error, setApiError] = useState<string | null>(null);
  const [waypointsCount, setWaypointsCount] = useState<number>(0);
  const [waypoints, setWaypoints] = useState<ChartWaypoint[]>([]);

  useEffect(() => {
    if (user?.entity_id && !chart && chart_id && api) {
      getChart(api, chart_id)
        .then((data) => {
          setChart(data);
          setLoadingChart(false);
        })
        .catch((error) => {
          console.log(error);
          navigate('/error', {
            state: {
              errorMessage: 'Failed to load chart',
              errorDetails:
                (error as Error).message ||
                'Unable to retrieve the requested chart. This could be due to network issues, server problems, or the chart may no longer exist.',
              returnPath: `/admin/charts/${chart_id}`,
            },
          });
        });
    }
  }, [api, user?.entity_id, chart, chart_id, navigate]);

  useEffect(() => {
    if (chart?.entity_id && api) {
      getChartWaypoints(api, chart.entity_id)
        .then((response) => {
          setWaypoints(response.results || []);
        })
        .catch((error) => {
          console.error('Error fetching waypoints:', error);
        });
    }
  }, [api, chart?.entity_id]);

  const handleDeleteChart = async (chart_id: EntityId): Promise<void> => {
    if (!api) return;
    try {
      await deleteChart(api, chart_id);
      if (chart && doc_types) {
        const doc_type = doc_types.find(
          (doc_type) => doc_type.entity_id === chart.document_type_id,
        );
        if (doc_type) {
          navigate({
            pathname: '/admin/charts',
            search: `?type=${doc_type.code}`,
          });
        }
      }
    } catch (error) {
      console.error('Error deleting chart:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to delete the chart. This could be due to network issues, server problems, or insufficient permissions.',
      );
    }
  };

  const handleApprove = async (): Promise<void> => {
    if (!chart || !api) return;
    try {
      setActionLoading(true);
      await approveChart(api, chart.entity_id);
      // Refresh the chart data
      const updatedChart = await getChart(api, chart.entity_id);
      setChart(updatedChart);
    } catch (error) {
      console.error('Error approving chart:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to approve the chart. This could be due to network issues, server problems, or insufficient permissions.',
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (): Promise<void> => {
    if (!chart || !api) return;
    try {
      setActionLoading(true);
      await rejectChart(api, chart.entity_id);
      // Refresh the chart data
      const updatedChart = await getChart(api, chart.entity_id);
      setChart(updatedChart);
    } catch (error) {
      console.error('Error rejecting chart:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to reject the chart. This could be due to network issues, server problems, or insufficient permissions.',
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateChartElements = async (): Promise<void> => {
    if (!chart || !api) return;
    try {
      setGenerating(true);
      await generateChartElements(api, chart.entity_id);
      // Refresh the chart data
      const updatedChart = await getChart(api, chart.entity_id);
      setChart(updatedChart);
    } catch (error) {
      console.error('Error generating chart elements:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to generate chart elements. This could be due to network issues, server problems, or the chart may be in an invalid state.',
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!chart || !api) return;
    try {
      setSubmitting(true);
      await submitChart(api, chart.entity_id);
      // Refresh the chart data
      const updatedChart = await getChart(api, chart.entity_id);
      setChart(updatedChart);
    } catch (error) {
      console.error('Error submitting chart:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to submit the chart. This could be due to network issues, server problems, or insufficient permissions.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading_chart || !chart) {
    return <LoadingMask />;
  }

  return (
    <Box>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Typography variant='h6' sx={{ mr: 4 }}>
          {chart.name} Chart
        </Typography>
        <Stack direction={'row'} spacing={2}>
          <Button
            onClick={() => {
              navigate({
                pathname: '/admin/charts/edit',
                search: `?chart_id=${chart.entity_id}`,
              });
            }}
            startIcon={<EditOutlinedIcon />}
          >
            Edit
          </Button>
          <Button
            onClick={() => setDeleteOpen(true)}
            startIcon={<DeleteOutlinedIcon />}
          >
            Delete
          </Button>
        </Stack>
      </Toolbar>
      <Divider />

      <Box padding='15px'>
        <Typography variant='subtitle1'>Basic Information</Typography>
        <Divider />
        <br />
        <Grid
          container
          component='dl' // mount a Definition List
          spacing={0}
          sx={{ display: 'table' }}
        >
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>ID:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {chart.entity_id}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Status:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {chart.status}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Created:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {toNiceTimestamp(chart.created_at, true)}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Reference Document:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {(
                chart as Chart & {
                  reference_document?: {
                    entity_id: string;
                    doc_id?: string;
                    name: string;
                  };
                }
              )?.reference_document ? (
                <Link
                  to={`/admin/documents/${(chart as Chart & { reference_document?: { entity_id: string } }).reference_document?.entity_id}`}
                  style={{ textDecoration: 'underline', color: '#1976d2' }}
                >
                  {
                    (
                      chart as Chart & {
                        reference_document?: { doc_id?: string; name: string };
                      }
                    ).reference_document?.doc_id
                  }{' '}
                  -{' '}
                  {
                    (chart as Chart & { reference_document?: { name: string } })
                      .reference_document?.name
                  }
                </Link>
              ) : null}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {chart.status === 'pending' && (
        <Box padding='15px'>
          <Typography variant='subtitle1'>Actions</Typography>
          <Divider />
          <br />
          <Stack direction='row' spacing={2}>
            <LoadingButton
              variant='contained'
              color='success'
              loading={actionLoading}
              onClick={handleApprove}
              disabled={actionLoading}
              startIcon={<CheckIcon />}
            >
              Approve
            </LoadingButton>
            <LoadingButton
              variant='contained'
              color='error'
              loading={actionLoading}
              onClick={handleReject}
              disabled={actionLoading}
              startIcon={<CloseIcon />}
            >
              Reject
            </LoadingButton>
          </Stack>
        </Box>
      )}

      {chart.status === 'draft' ? (
        <Box padding='15px'>
          <Stack direction='row' spacing={2}>
            <LoadingButton
              variant='outlined'
              loading={generating}
              onClick={handleGenerateChartElements}
              disabled={generating || waypointsCount === 0}
              startIcon={<BuildOutlinedIcon />}
            >
              Generate Chart Elements
            </LoadingButton>
            <LoadingButton
              variant='outlined'
              loading={submitting}
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={<SendOutlinedIcon />}
            >
              Submit For Approval
            </LoadingButton>
          </Stack>
        </Box>
      ) : undefined}

      <Box padding='15px'>
        <ChartWaypoints
          chartId={chart.entity_id}
          onWaypointsCountChange={setWaypointsCount}
          isEditable={chart.status === 'draft' || chart.status === 'pending'}
        />
      </Box>

      {(chart.status === 'draft' ||
        chart.status === 'pending' ||
        chart.status === 'active') && (
        <Box padding='15px'>
          <ChartContents
            chartId={chart.entity_id}
            chartStatus={chart.status}
            waypoints={waypoints}
          />
        </Box>
      )}

      <ChartDeleteDialog
        chart_id={chart.entity_id}
        deleteChart={handleDeleteChart}
        open={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
      />

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </Box>
  );
}

export default AdminChart;
